import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Mail, Phone } from 'lucide-react';
import Navbar from '@/components/Navbar';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
                  <p className="text-zinc-500">Last updated: December 2025</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              <div className="glass-card rounded-xl p-8 space-y-8">
                
                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    Welcome to Blackified ("we," "our," or "us"). We are committed to protecting your personal information 
                    and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                    your information when you use our platform.
                  </p>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    By using Blackified, you agree to the collection and use of information in accordance with this policy. 
                    If you do not agree with the terms of this privacy policy, please do not access the platform.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
                  
                  <h3 className="text-lg font-medium text-white mt-6 mb-3">2.1 Personal Information</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    We may collect personally identifiable information that you voluntarily provide when you:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li>Register for an account (name, email address, profile picture)</li>
                    <li>Submit a business listing (business name, address, contact information)</li>
                    <li>Complete identity verification (government-issued ID, address verification documents)</li>
                    <li>Make purchases or transactions (payment information, billing address)</li>
                    <li>Connect your cryptocurrency wallet (wallet address)</li>
                    <li>Contact us for support</li>
                  </ul>

                  <h3 className="text-lg font-medium text-white mt-6 mb-3">2.2 Automatically Collected Information</h3>
                  <p className="text-zinc-400 leading-relaxed">
                    When you access our platform, we automatically collect certain information, including:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li>Device information (device type, operating system, browser type)</li>
                    <li>Log data (IP address, access times, pages viewed)</li>
                    <li>Location data (with your permission, for local business searches)</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li>Create and manage your account</li>
                    <li>Process business listings and verifications</li>
                    <li>Facilitate transactions and Melanin token operations</li>
                    <li>Provide location-based business recommendations</li>
                    <li>Send notifications about your account, businesses, and rewards</li>
                    <li>Respond to your inquiries and support requests</li>
                    <li>Prevent fraud and ensure platform security</li>
                    <li>Comply with legal obligations</li>
                    <li>Improve our services and user experience</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">4. Information Sharing</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    We may share your information in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li><strong className="text-white">Business Information:</strong> Your submitted business details are publicly displayed in our directory</li>
                    <li><strong className="text-white">Service Providers:</strong> With third-party vendors who help us operate the platform (payment processors, identity verification services, cloud hosting)</li>
                    <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
                    <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  </ul>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    We do not sell your personal information to third parties.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">5. Data Security</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    We implement appropriate technical and organizational security measures to protect your personal information, 
                    including encryption of sensitive data, secure servers, and regular security assessments. However, no method 
                    of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    Depending on your location, you may have the following rights:
                  </p>
                  <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion of your data</li>
                    <li>Object to processing of your data</li>
                    <li>Data portability</li>
                    <li>Withdraw consent</li>
                  </ul>
                  <p className="text-zinc-400 leading-relaxed mt-3">
                    To exercise these rights, please contact us at the information provided below.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">7. Cookies</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    We use cookies and similar tracking technologies to enhance your experience. You can control cookies 
                    through your browser settings. Essential cookies are required for the platform to function properly.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">8. Children's Privacy</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    Our platform is not intended for children under 13 years of age. We do not knowingly collect personal 
                    information from children under 13. If you believe we have collected information from a child under 13, 
                    please contact us immediately.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h2>
                  <p className="text-zinc-400 leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
                    new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the platform 
                    after any changes constitutes acceptance of the new Privacy Policy.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-4">10. Contact Us</h2>
                  <p className="text-zinc-400 leading-relaxed mb-4">
                    If you have questions about this Privacy Policy or our privacy practices, please contact us:
                  </p>
                  <div className="flex flex-col gap-3">
                    <a href="mailto:privacy@blackified.com" className="flex items-center gap-2 text-purple-400 hover:text-purple-300">
                      <Mail className="w-4 h-4" />
                      privacy@blackified.com
                    </a>
                    <a href="tel:+12166267560" className="flex items-center gap-2 text-purple-400 hover:text-purple-300">
                      <Phone className="w-4 h-4" />
                      (216) 626-7560
                    </a>
                  </div>
                </section>

              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            © 2025 Blackified. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-zinc-400 hover:text-white text-sm">
              Terms of Service
            </Link>
            <Link to="/help" className="text-zinc-400 hover:text-white text-sm">
              Help Center
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
